import { Popover } from '@mantine/core'
import { DatePicker } from '@mantine/dates'
import dayjs from 'dayjs'
import { useState } from 'react'
import styled from 'styled-components'

import { TextInput } from '../text-input'
import { Icon } from '../icon'
import { DateInputProps } from './date-input.types'

const StyledInputWrapper = styled.div<{ $disabled?: boolean }>`
  position: relative;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.gray80};
  border-radius: ${({ theme }) => theme.borderRadii.medium};

  ${({ theme }) => `
    input {
      padding-left: ${theme.sizes[10]};
    }
  `}

  ${({ theme, $disabled }) =>
    $disabled
      ? `
    input:read-only {
      cursor: not-allowed;
      background-color: ${theme.colors.gray80};
      border: 1px solid ${theme.colors.gray80};
      color: ${theme.colors.gray100};
    }
  `
      : `
    input:read-only {
      cursor: pointer;
      background-color: ${theme.colors.white};
      border: 1px solid ${theme.colors.gray80};

      &:hover {
        background-color: ${theme.colors.white};
        border-color: ${theme.colors.black};
        outline: ${theme.sizes[1]} solid ${theme.colors.gray80};
      }

      &:focus,
      &:active {
        border-color: ${theme.colors.black};
      }
    }
  `}
`

const StyledClearButton = styled.button`
  ${({ theme }) => `
    position: absolute;
    top: 0;
    bottom: 0;
    right: ${theme.sizes[2]};
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    cursor: pointer;
  `}
`

const StyledCalendarContainer = styled.div`
  ${({ theme }) => `
    .mantine-DatePicker-calendarHeaderLevel {
      font-size: ${theme.fontSizes.bodySmall};
      font-weight: ${theme.fontWeights.medium};
      color: ${theme.colors.text_primary};
    }

    .mantine-DatePicker-calendarHeaderControl {
      color: ${theme.colors.text_primary};
    }
    
    .mantine-DatePicker-weekday {
      color: ${theme.colors.gray600};
      font-weight: ${theme.fontWeights.medium};
    }

    .mantine-DatePicker-day {
      width: ${theme.sizes[8]};
      height: ${theme.sizes[8]};
      color: ${theme.colors.text_primary};

      &[data-selected] {
        background-color: ${theme.colors.primary400};
        color: ${theme.colors.white};
        font-weight: ${theme.fontWeights.medium};
      }
      
      &[data-outside] {
        color: ${theme.colors.gray100};
        font-weight: ${theme.fontWeights.medium};
        opacity: 1;
      }
    }
  `}
`

// Mantine's calendar is local-time; in `utc` mode we convert only at the boundary.
const toLocalDay = (
  date: Date | null | undefined,
  utc: boolean
): Date | null => {
  if (!date) return null
  return utc
    ? new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
    : date
}

const fromLocalDay = (date: Date | null, utc: boolean): Date | null => {
  if (!date || !utc) return date
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
}

export function DateInput({
  name,
  value,
  onChange,
  onBlur,
  placeholder = 'DD/MM/YYYY',
  status = 'default',
  disabled = false,
  clearable = false,
  dateFormat = 'DD/MM/YYYY',
  'data-testid': testId,
  withinPopup = false,
  minDate,
  maxDate,
  maxLevel = 'year',
  utc = false,
  zIndex = 9999,
  ...restProps
}: DateInputProps) {
  const [isOpen, setIsOpen] = useState(false)

  const localValue = toLocalDay(value, utc)

  const handleDateChange = (date: Date | null) => {
    onChange(fromLocalDay(date, utc))
    setIsOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
  }

  const formattedValue = localValue ? dayjs(localValue).format(dateFormat) : ''

  return (
    <Popover
      opened={isOpen}
      position="bottom-start"
      shadow="sm"
      onClose={() => setIsOpen(false)}
      onDismiss={() => setIsOpen(false)}
      withinPortal={!withinPopup}
      zIndex={zIndex}
    >
      <Popover.Target>
        <StyledInputWrapper
          $disabled={disabled}
          data-testid={`${testId ?? name}-popover-target`}
        >
          <TextInput
            name={name}
            value={formattedValue}
            placeholder={placeholder}
            status={status}
            disabled={disabled}
            readOnly
            onClick={() => !disabled && setIsOpen(true)}
            onBlur={onBlur}
            icon="calendar"
            data-testid={testId ?? name}
          />
          {clearable && value && !disabled && (
            <StyledClearButton
              type="button"
              onClick={handleClear}
              data-testid={`${testId ?? name}-clear`}
              aria-label="Clear date"
            >
              <Icon name="close" color="gray400" size={4} />
            </StyledClearButton>
          )}
        </StyledInputWrapper>
      </Popover.Target>
      <Popover.Dropdown>
        <StyledCalendarContainer>
          <DatePicker
            value={localValue}
            onChange={handleDateChange}
            minDate={toLocalDay(minDate, utc) ?? undefined}
            maxDate={toLocalDay(maxDate, utc) ?? undefined}
            maxLevel={maxLevel}
            {...restProps}
          />
        </StyledCalendarContainer>
      </Popover.Dropdown>
    </Popover>
  )
}
